import { computed, reactive } from 'vue';
import type { NativeUpdateCheck, NativeUpdater } from '../model.js';

type Phase =
  | 'idle'
  | 'checking'
  | 'available'
  | 'current'
  | 'unavailable'
  | 'downloading'
  | 'installing'
  | 'restarting'
  | 'error';
export function createNativeUpdateController(updater: NativeUpdater | undefined) {
  const state = reactive<{
    phase: Phase;
    result: NativeUpdateCheck | undefined;
    error: string;
    downloaded: number;
    total: number | undefined;
  }>({ phase: 'idle', result: undefined, error: '', downloaded: 0, total: undefined });
  const busy = computed(() =>
    ['checking', 'downloading', 'installing', 'restarting'].includes(state.phase),
  );
  async function check() {
    if (!updater || busy.value) return;
    state.phase = 'checking';
    state.error = '';
    try {
      state.result = await updater.check();
      state.phase = state.result.unavailableReason
        ? 'unavailable'
        : state.result.update
          ? 'available'
          : 'current';
    } catch {
      state.result = undefined;
      state.error = 'Could not check for updates. Check your connection and try again.';
      state.phase = 'error';
    }
  }
  async function install() {
    const offer = state.result?.update;
    if (!updater || !offer || state.phase !== 'available') return;
    state.phase = 'downloading';
    state.downloaded = 0;
    state.total = undefined;
    try {
      await updater.install(offer.id, (progress) => {
        if (state.phase !== 'downloading' && state.phase !== 'installing') return;
        state.phase = progress.phase;
        state.downloaded = progress.downloaded;
        state.total = progress.total;
      });
      state.phase = 'restarting';
    } catch {
      state.result = undefined;
      state.error = 'The update could not be installed. Check for updates to retry.';
      state.phase = 'error';
    }
  }
  return { state, busy, check, install };
}

const controllers = new WeakMap<NativeUpdater, ReturnType<typeof createNativeUpdateController>>();
/** Retain a single update transaction while the person navigates away from Settings. */
export function useNativeUpdates(updater: NativeUpdater | undefined) {
  if (!updater) return createNativeUpdateController(undefined);
  const existing = controllers.get(updater);
  if (existing) return existing;
  const created = createNativeUpdateController(updater);
  controllers.set(updater, created);
  return created;
}
